import React, { useState } from 'react';
import { Send, Mail, MapPin, Linkedin, Github, Instagram, Sparkles, Heart, Rocket } from 'lucide-react';
import { personalInfo, socialLinks } from '../data/mock';
import { useAchievements } from './AchievementSystem';

const iconMap = { Linkedin, Github, Instagram };

const ContactSection = () => {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  
  // Try to get achievements context
  let unlockAchievement = () => {};
  try {
    const achievements = useAchievements();
    if (achievements) {
      unlockAchievement = achievements.unlockAchievement;
    }
  } catch (e) {}

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch(`/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Failed to send message');
      }

      setSubmitted(true);
      unlockAchievement('contact_hero');
      setFormData({ name: '', email: '', message: '' });
      setTimeout(() => setSubmitted(false), 4000);
    } catch (err) {
      setError('Oops! Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-gradient-to-b from-pink-50 via-purple-50 to-blue-100 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-10 left-10 text-6xl opacity-20 animate-float">📬</div>
      <div className="absolute bottom-10 right-10 text-6xl opacity-20 animate-float" style={{ animationDelay: '1s' }}>💌</div>
      <div className="absolute top-1/2 right-20 text-4xl opacity-20 animate-float" style={{ animationDelay: '0.5s' }}>✨</div>
      
      <div className="max-w-4xl mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white shadow-lg mb-4">
            <span className="text-2xl">🤝</span>
            <span className="font-bold text-purple-600">Team Up!</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-black text-gray-800">
            Let's{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
              Connect!
            </span>
          </h2>
          <p className="text-gray-500 mt-4 text-lg">
            Got a mission? Let's save the world together! 🌍
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Contact Info */}
          <div className="space-y-6">
            {/* Info Cards */}
            <div className="bg-white rounded-3xl p-6 shadow-xl border-4 border-purple-100 hover:border-purple-300 transition-all">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-2xl shadow-lg">
                  📧
                </div>
                <div>
                  <p className="text-gray-500 text-sm font-medium">Send a Signal</p>
                  <p className="text-gray-800 font-bold">{personalInfo.email}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-3xl p-6 shadow-xl border-4 border-blue-100 hover:border-blue-300 transition-all">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center text-2xl shadow-lg">
                  📍
                </div>
                <div>
                  <p className="text-gray-500 text-sm font-medium">Hero Base</p>
                  <p className="text-gray-800 font-bold">{personalInfo.location}</p>
                </div>
              </div>
            </div>

            {/* Social Links */}
            <div className="bg-white rounded-3xl p-6 shadow-xl border-4 border-orange-100">
              <p className="text-gray-500 text-sm font-medium mb-4">Find me on the web! 🌐</p>
              <div className="flex gap-4">
                {socialLinks.map((link) => {
                  const Icon = iconMap[link.icon];
                  return (
                    <a
                      key={link.name}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center text-purple-600 hover:from-purple-500 hover:to-pink-500 hover:text-white hover:scale-110 transition-all shadow-lg"
                    >
                      {Icon && <Icon className="w-6 h-6" />}
                    </a>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-3xl p-8 shadow-2xl border-4 border-purple-200">
            {submitted ? (
              <div className="flex flex-col items-center justify-center h-full py-8" data-testid="contact-success">
                <div className="text-6xl mb-4 animate-bounce">🎉</div>
                <h3 className="text-2xl font-black text-gray-800 mb-2">Message Sent!</h3>
                <p className="text-gray-500 text-center">Thanks for reaching out, hero! I'll respond soon! 🚀</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                {error && (
                  <div className="bg-red-100 border-2 border-red-300 rounded-2xl p-4 text-red-600 font-medium flex items-center gap-2" data-testid="contact-error">
                    <span>⚠️</span> {error}
                  </div>
                )}
                <div>
                  <label className="block text-gray-700 font-bold mb-2">Your Name 🦸</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    data-testid="contact-name-input"
                    className="w-full px-5 py-4 rounded-2xl border-3 border-purple-200 focus:border-purple-500 focus:outline-none focus:ring-4 focus:ring-purple-100 transition-all font-medium"
                    placeholder="What should I call you?"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 font-bold mb-2">Your Email 📧</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    data-testid="contact-email-input"
                    className="w-full px-5 py-4 rounded-2xl border-3 border-purple-200 focus:border-purple-500 focus:outline-none focus:ring-4 focus:ring-purple-100 transition-all font-medium"
                    placeholder="Where can I reach you?"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 font-bold mb-2">Your Message 💬</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={4}
                    data-testid="contact-message-input"
                    className="w-full px-5 py-4 rounded-2xl border-3 border-purple-200 focus:border-purple-500 focus:outline-none focus:ring-4 focus:ring-purple-100 transition-all resize-none font-medium"
                    placeholder="What's the mission?"
                  />
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  data-testid="contact-submit-btn"
                  className="w-full py-4 bg-gradient-to-r from-purple-600 via-pink-600 to-orange-500 rounded-2xl text-white font-black text-lg shadow-xl hover:shadow-2xl hover:scale-[1.02] transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <span className="w-6 h-6 border-3 border-white/30 border-t-white rounded-full animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Rocket className="w-5 h-5" />
                      Launch Message! 🚀
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
